import { gql, useQuery } from '@apollo/client';
import useAppSelector from '../app/redux/useAppSelector';
import type {
  AllowUserGroupQuery,
  AllowUserGroupQueryVariables,
  GroupPolicyCheck_GroupFragment,
  GroupPolicyCheck_UserFragment,
} from '../graphql/generated/graphqlTypes';
import { UserType } from '../graphql/generated/graphqlTypes';

export enum GroupPolicyAction {
  CreateGroup,
  RequestGroup,
  DeleteGroup,
  EditMembership,
  JoinGroup,
  RequestMembership,
  UpdateGroup,
  LeaveGroup,
}

const GROUP_POLICY_CHECK_USER_FRAGMENT = gql`
  fragment groupPolicyCheck_user on User {
    id
    type
  }
`;

const GROUP_POLICY_CHECK_GROUP_FRAGMENT = gql`
  fragment groupPolicyCheck_group on Group {
    id
    owners {
      id
    }
    members {
      id
    }
    requestors {
      id
    }
  }
`;

const ALLOW_USER_GROUP_QUERY = gql`
  query AllowUserGroup {
    session: currentSession {
      organization {
        id
        settings {
          allowUserGroupCreation
        }
      }
    }
  }
`;

export function useGroupPolicyCheck(
  action: GroupPolicyAction,
  group?: GroupPolicyCheck_GroupFragment | null,
) {
  const user = useAppSelector((state) => state.session.user);
  const { data } = useQuery<AllowUserGroupQuery, AllowUserGroupQueryVariables>(
    ALLOW_USER_GROUP_QUERY,
  );

  const isUserGroupCreationAllowed =
    data?.session.organization.settings?.allowUserGroupCreation ?? false;

  return groupPolicyCheck(action, user, group, isUserGroupCreationAllowed);
}

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const findUserId = (arr: any, user: any) =>
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  !!(user?.id && arr?.find((u: any) => u?.id === user?.id));

const isOwner = (
  group: GroupPolicyCheck_GroupFragment,
  user: GroupPolicyCheck_UserFragment,
) => findUserId(group?.owners, user);

const isMember = (
  group: GroupPolicyCheck_GroupFragment,
  user: GroupPolicyCheck_UserFragment,
) => findUserId(group?.members, user);

const isRequestor = (
  group: GroupPolicyCheck_GroupFragment,
  user: GroupPolicyCheck_UserFragment,
) => findUserId(group?.requestors, user);

export const isUserInGroup = (
  group: GroupPolicyCheck_GroupFragment,
  user: GroupPolicyCheck_UserFragment,
) => isOwner(group, user) || isMember(group, user);

function groupPolicyCheck(
  action: GroupPolicyAction,
  user: GroupPolicyCheck_UserFragment,
  group?: GroupPolicyCheck_GroupFragment | null,
  allowUserGroupCreation?: boolean,
) {
  if (action === GroupPolicyAction.CreateGroup) {
    return allowUserGroupCreation
      ? user.type !== UserType.Guest
      : user.type === UserType.Admin;
  }
  if (action === GroupPolicyAction.RequestGroup) {
    return user.type !== UserType.Guest;
  }

  if (!group) {
    return false;
  }

  switch (action) {
    case GroupPolicyAction.JoinGroup:
      return (
        !(isOwner(group, user) || isMember(group, user)) &&
        user.type === UserType.Admin
      );
    case GroupPolicyAction.EditMembership:
    case GroupPolicyAction.UpdateGroup:
    case GroupPolicyAction.DeleteGroup:
      return isOwner(group, user);
    case GroupPolicyAction.LeaveGroup:
      return isOwner(group, user) || isMember(group, user);
    case GroupPolicyAction.RequestMembership:
      return (
        user.type !== UserType.Guest &&
        !isUserInGroup(group, user) &&
        !isRequestor(group, user)
      );
    default:
      return false;
  }
}

groupPolicyCheck.fragments = {
  group: GROUP_POLICY_CHECK_GROUP_FRAGMENT,
  user: GROUP_POLICY_CHECK_USER_FRAGMENT,
};

useGroupPolicyCheck.fragments = {
  group: gql`
    fragment useGroupPolicyCheck_group on Group {
      ...groupPolicyCheck_group
    }
    ${GROUP_POLICY_CHECK_GROUP_FRAGMENT}
  `,
};
